import { useParams, useSearchParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { BodyContainer } from '../../../../elements';
import { OrderAllocationHook, OrderDetailHook } from '../../../../hooks/order';
import { useAppHeaderLead } from '../../../../hooks/useAppHeaderLead';
import { useHeaderActions } from '../../../../hooks/useHeaderActions';
import {
  CreateLabel,
  CustomerDeatils,
  FulfillOrderPopup,
  LabelDetails,
  OrderDetailHeaderActions,
  OrderDetailHeaderLead,
  PickingList,
} from '../../../../page-components/order';
import {
  AllocationService,
  OrderService,
  resumePausedOrders,
  ToastMessage,
  OrderStatus,
} from '../../../../utility';
import { CancelOrderPopup } from '../../../../page-components/order/CancelOrderPopup';
import { EditAddressPopup } from '../../../../page-components/packaging/detail/edit-address';
import { PackagingService } from '../../../../utility/services/packaging';
import { ConfirmationPopup } from '../../../../components';
import { useGlobalContext } from '../../../../contexts';
import { Alert, Button, Space } from 'antd';

function getPauseReasonLabel(reason) {
  if (reason === 'non_pickable_allocations') {
    return 'Inventory allocated in non-picking location';
  }
  return reason || 'Fulfillment paused';
}

const isFulfillmentPaused = data => data?.wms_fulfillment_pause?.status === 'paused';

const isAllocationShipped = allocation =>
  !!allocation?.shippedAt ||
  allocation?.status === 'completed' ||
  allocation?.logs?.some(log => log?.action === 'shipped' || log?.status === 'Shipped');

const getLabelAllocationIds = label => {
  const rawIds =
    label?.metadata?.allocationIds ||
    label?.metadata?.allocation_ids ||
    label?.allocationIds ||
    [];

  return Array.isArray(rawIds) ? rawIds.map(String) : [];
};

const getAllocationId = allocation =>
  String(allocation?.allocationId || allocation?._id || '');

const getMatchingAllocationsForLabel = (allocations, labelAllocationIds) => {
  if (!Array.isArray(allocations) || !labelAllocationIds.length) {
    return [];
  }

  const allocationIdsToMatch = new Set(labelAllocationIds);
  const matchingAllocations = allocations.filter(item =>
    allocationIdsToMatch.has(getAllocationId(item)),
  );
  const matchedAllocationIds = new Set(matchingAllocations.map(getAllocationId));

  return labelAllocationIds.every(id => matchedAllocationIds.has(id))
    ? matchingAllocations
    : [];
};

const cancellationTypes = {
  cancel: 'cancel',
};

export function OrderDetailScreen() {
  const { id } = useParams();
  const { warehouseId } = useGlobalContext();
  const [searchParams] = useSearchParams();
  const providerId = searchParams.get('providerId');
  const { data, refresh, loading: orderLoading } = OrderDetailHook(id, providerId);
  const {
    data: pickList,
    loading: pickListLoading,
    refresh: refreshPickList,
  } = OrderAllocationHook(id);

  const [openFulfill, setOpenFulfill] = useState(false);
  const [openCancelPopup, setOpenCancelPopup] = useState(false);
  const [loading, setLoading] = useState(false);

  const [showCreateLabel, setShowCreateLabel] = useState(false);
  const [labelToCorrect, setLabelToCorrect] = useState(null);
  const [showAddressPopup, setShowAddressPopup] = useState(false);
  const [showRefundPopup, setShowRefundPopup] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [confirmationPopup, setConfirmationPopup] = useState({
    open: false,
    item: null,
    type: null,
  });

  const [refunding, setRefunding] = useState(false);

  const pause = data?.wms_fulfillment_pause;
  const fulfillmentPaused = isFulfillmentPaused(data);
  const pauseReason = getPauseReasonLabel(pause?.reason);
  const blockingItems = Array.isArray(pause?.blockingItems) ? pause.blockingItems : [];
  const labelableAllocations = useMemo(
    () =>
      Array.isArray(pickList) ? pickList.filter(item => !isAllocationShipped(item)) : [],
    [pickList],
  );
  const correctionLabelAllocationIds = useMemo(() => {
    return getLabelAllocationIds(labelToCorrect);
  }, [labelToCorrect]);
  const correctionPickList = useMemo(() => {
    if (!labelToCorrect || !Array.isArray(pickList)) {
      return labelableAllocations;
    }

    if (!correctionLabelAllocationIds.length) {
      return [];
    }

    return getMatchingAllocationsForLabel(pickList, correctionLabelAllocationIds);
  }, [correctionLabelAllocationIds, labelToCorrect, labelableAllocations, pickList]);
  const createLabelDisabled =
    data?.states?.includes?.(OrderStatus.shipped) ||
    (Array.isArray(pickList) && pickList.length > 0 && !labelableAllocations.length);
  const createLabelDisabledReason =
    'This order has already-shipped allocations. Use the label correction/refund flow instead of creating a new standalone label.';

  const refreshAll = () => {
    refresh();
    refreshPickList();
  };

  const resumeFulfillment = async () => {
    try {
      setResumeLoading(true);
      const result = await resumePausedOrders({
        warehouseId,
        orderIds: [id],
      });
      if (result.ok) {
        refreshAll();
      }
    } finally {
      setResumeLoading(false);
    }
  };

  const fulfillOrder = async () => {
    try {
      setLoading(true);
      const payload = {
        action: 'fulfill',
      };
      await OrderService.orderAction(id, payload);
      closePopup();
      ToastMessage.success('Order fulfilled successfully.');
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  };

  const openPopup = () => {
    setOpenFulfill(true);
  };

  const canelPopup = () => {
    setOpenCancelPopup(true);
  };

  const closePopup = () => {
    setOpenFulfill(false);
  };

  const closeCancelPopup = () => {
    setOpenCancelPopup(false);
  };

  const openCreateLabel = () => {
    if (createLabelDisabled) {
      ToastMessage.error(createLabelDisabledReason);
      return;
    }
    setShowCreateLabel(true);
  };

  const closeCreateLabel = () => {
    setShowCreateLabel(false);
    setLabelToCorrect(null);
  };

  const openLabelCorrection = label => {
    if (!label?._id) {
      ToastMessage.error('Unable to correct label without a label id.');
      return;
    }
    const labelAllocationIds = getLabelAllocationIds(label);
    if (!labelAllocationIds.length) {
      ToastMessage.error('Unable to correct label without allocation mapping.');
      return;
    }
    if (!getMatchingAllocationsForLabel(pickList, labelAllocationIds).length) {
      ToastMessage.error('Unable to find all allocations for this label.');
      return;
    }
    setLabelToCorrect(label);
    setShowCreateLabel(true);
  };

  const openAddressPopup = () => {
    setShowAddressPopup(true);
  };

  const closeAddressPopup = () => {
    setShowAddressPopup(false);
  };

  const openRefundPopup = label => {
    setShowRefundPopup(label);
  };

  const closeRefundPopup = () => {
    setShowRefundPopup(null);
  };

  const confirmRefundLabel = async () => {
    try {
      setRefunding(true);
      await PackagingService.refundLabel(showRefundPopup?._id);
      ToastMessage.success('Label Refunded Successfully');
      closeRefundPopup();
      refresh();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setRefunding(false);
    }
  };

  const cancelAllocation = item => {
    setConfirmationPopup({
      open: true,
      type: cancellationTypes.cancel,
      item,
    });
  };

  const onCloseConfirmation = () => {
    setConfirmationPopup({
      open: false,
    });
  };

  const onConfirm = async () => {
    try {
      setProcessing(true);
      if (confirmationPopup.type === cancellationTypes.cancel) {
        await AllocationService.cancelAllocation(confirmationPopup.item.allocationId);
        ToastMessage.success('Allocation cancelled successfully');
      }
      onCloseConfirmation();
      refreshPickList();
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setProcessing(false);
    }
  };

  useAppHeaderLead(
    () => <OrderDetailHeaderLead data={data} loading={orderLoading} />,
    [data, orderLoading],
  );

  useHeaderActions(
    () => (
      <OrderDetailHeaderActions
        showAction
        openPopup={openPopup}
        cancelOrder={canelPopup}
        data={data}
        createLabel={openCreateLabel}
        createLabelDisabled={createLabelDisabled}
        createLabelDisabledReason={createLabelDisabledReason}
        openAddressPopup={openAddressPopup}
      />
    ),
    [
      data,
      openPopup,
      canelPopup,
      openCreateLabel,
      createLabelDisabled,
      createLabelDisabledReason,
      openAddressPopup,
    ],
  );

  return (
    <BodyContainer className="no-header">
      {fulfillmentPaused ? (
        <Alert
          className="mb-3"
          type="warning"
          showIcon
          message="Fulfillment is paused"
          description={
            <div>
              <div>Reason: {pauseReason}</div>
              <div>
                Complete the suggested transfer work, then recalculate fulfillment.
              </div>
              {blockingItems.length ? (
                <div className="mt-2">
                  Blocking items:{' '}
                  {blockingItems
                    .map(item =>
                      [
                        item?.sku || item?.title || item?.itemId || item?.productId,
                        item?.locationName || item?.locationCode,
                      ]
                        .filter(Boolean)
                        .join(' at '),
                    )
                    .filter(Boolean)
                    .join(', ')}
                </div>
              ) : null}
              <Space className="mt-2">
                <Button
                  size="small"
                  type="primary"
                  loading={resumeLoading}
                  onClick={resumeFulfillment}
                >
                  Recalculate Fulfillment
                </Button>
                <Button size="small" onClick={refreshAll}>
                  Refresh
                </Button>
              </Space>
            </div>
          }
        />
      ) : null}
      <CustomerDeatils loading={orderLoading} data={data} />
      <div className="row mt-4">
        <PickingList
          loading={pickListLoading}
          fallbackData={data}
          data={pickList}
          cancelAllocation={cancelAllocation}
        />
        <LabelDetails
          openRefundPopup={openRefundPopup}
          openLabelCorrection={openLabelCorrection}
          loading={orderLoading || pickListLoading}
          data={data}
        />
      </div>
      <FulfillOrderPopup
        open={openFulfill}
        onClose={closePopup}
        onConfirm={fulfillOrder}
        loading={loading}
      />
      <CancelOrderPopup
        open={openCancelPopup}
        onClose={closeCancelPopup}
        refresh={refresh}
        orderId={id}
      />
      <CreateLabel
        showCreateLabel={showCreateLabel}
        closeCreateLabel={closeCreateLabel}
        data={data}
        refresh={refresh}
        pickList={labelToCorrect ? correctionPickList : labelableAllocations}
        providerId={providerId}
        isLabelCorrection={!!labelToCorrect}
        labelToCorrect={labelToCorrect}
      />
      <EditAddressPopup
        address={data?.shippingAddress}
        open={showAddressPopup}
        onClose={closeAddressPopup}
        orderId={id}
        refresh={refresh}
        labels={data?.labels || []}
      />
      <ConfirmationPopup
        title="Refund Label"
        message={`Are you sure to refund label with tracking id ${showRefundPopup?.tracking_id}`}
        onClose={closeRefundPopup}
        loading={refunding}
        onConfirm={confirmRefundLabel}
        open={!!showRefundPopup}
      />
      <ConfirmationPopup
        open={confirmationPopup.open}
        title={`${
          confirmationPopup.type === cancellationTypes.cancel ? 'Cancel' : 'Remove'
        } ${confirmationPopup?.item?.inventory?.product.sku} Allocation`}
        message={
          confirmationPopup.type === cancellationTypes.cancel
            ? 'This will remove allocation and item from the wave.'
            : 'This will remove the allocation but keep the item'
        }
        onClose={onCloseConfirmation}
        onConfirm={onConfirm}
        loading={processing}
        closeText="Close"
      />
    </BodyContainer>
  );
}
